import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EngagementEvent } from '../../mockData';
import { BookingConfirmation } from '../../../../components/BookingConfirmation/BookingConfirmation';
import { useEventBooking } from '../../../../hooks/useEventBooking';
import { useEvents } from '../../../../hooks/useEvents';
import LinkIcon from '../../../../assets/icons/link-icon.svg';
import DetailsArrow from '../../../../assets/icons/details-arrow.svg';
import CameraIcon from '../../../../assets/icons/camera-icon.svg';
import LunchLearnIcon from '../../../../assets/icons/lunch-learn-icon.svg';
import StoreIcon from '../../../../assets/icons/store-icon.svg';
import formIconCeu from '../../../../assets/icons/form-icon-ceu.svg';
import formIconInfo from '../../../../assets/icons/form-icon-info.svg';
import formIconConfirmReached from '../../../../assets/icons/form-icon-confirm-reached.svg';
import formIconConfirmNotReached from '../../../../assets/icons/form-icon-confirm-not-reached.svg';
import { MultiStepForm } from '../../../../components/MultiStepForm/MultiStepForm';
import { CompletedStep } from '../../../../components/MultiStepForm/steps/CompletedStep';
import { UserInfoStep } from '../../../../components/MultiStepForm/steps/UserInfoStep';
import { ConfirmationStep } from '../../../../components/MultiStepForm/steps/ConfirmationStep';
import { EventDetailsModal } from '../EventDetailsModal/EventDetailsModal';
import { EventType, CityType, ShowroomType, EventImageType } from '../../../../types/events';
import { normalizeEventType } from '../../../../types/event';
import defaultEventImage from '../../../../assets/backgroundImages/eventpageCardImages/CEU.png';
import './EngagementEvents.scss';

interface EventCardProps {
  event: EngagementEvent;
  onRegister: (event: EngagementEvent) => void;
  isAttended?: boolean;
  isBooked?: boolean;
}

const SHOW_REGISTER_EVENTS = import.meta.env.VITE_SHOW_EVENTS === "true";

const EventCard: React.FC<EventCardProps> = ({ event, onRegister, isAttended, isBooked }) => {
  const { t } = useTranslation(['home', 'events']);
  const { isPending } = useEventBooking();
  
  const getActionButton = () => {
    switch (event.actionType) {
      case 'video':
        return (
          <a 
            href="https://subzero.csod.com/login/render.aspx?id=defaultclp&CLP=false"
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-event-card__action dashboard-event-card__action--video"
          >
            {t('engagement.goToVideo')}
            <img src={LinkIcon} className="dashboard-event-card__action-icon" alt="" />
          </a>
        );
      case 'details':
        return (
          <button className="dashboard-event-card__action dashboard-event-card__action--details">
            {t('engagement.details', 'Details')}
            <img src={DetailsArrow} className="dashboard-event-card__action-icon" alt="" />
          </button>
        );
      case 'register':
        return (
          <button 
            className={`dashboard-event-card__action dashboard-event-card__action--register ${isAttended ? 'dashboard-event-card__action--attended' : isBooked ? 'dashboard-event-card__action--booked' : ''}`}
            onClick={() => onRegister(event)}
            disabled={isBooked || isAttended || isPending}
          >
            {isAttended ? t('events:eventCard.booked') :
             isBooked ? 
              t('events:eventCard.booked') : 
              isPending ? 
                t('events:eventCard.registering', 'Registering...') : 
                t('events:eventCard.register', 'Register')}
            {!isBooked && !isAttended && <img src={DetailsArrow} className="dashboard-event-card__action-icon" alt="" />}
          </button>
        );
    }
  };

  return (
    <div className="dashboard-event-card">
      <div className="dashboard-event-card__icon">
        <img
          src={{
            'training': CameraIcon,
            'lunch-learn': LunchLearnIcon,
            'store': StoreIcon
          }[event.eventType]}
          alt=""
          className="dashboard-event-card__icon-image"
        />
      </div>
      <div className="dashboard-event-card__content">
        <h3 className="dashboard-event-card__title">{event.title}</h3>
        <p className="dashboard-event-card__points">
          {t('engagement.earn')} <span className="number">{event.points.toLocaleString()}</span> {t('common.points')}
        </p>
      </div>
      <div className="dashboard-event-card__action-wrapper">
        {getActionButton()}
      </div>
    </div>
  );
};

export const EngagementEvents: React.FC = () => {
  const { t, i18n } = useTranslation(['home', 'events']);
  const [searchParams] = useSearchParams();
  
  // Check for testingAppointments query parameter to bypass environment variable
  const isTestingMode = searchParams.get('testingAppointments') === 'true';
  const shouldShowEvents = SHOW_REGISTER_EVENTS || isTestingMode;
  
  // Only fetch events if the feature is enabled or testing mode is active
  const { data: apiEvents, isLoading, error } = shouldShowEvents ? useEvents() : { data: [], isLoading: false, error: null };
  const { bookEvent } = useEventBooking();
  
  // Get current language
  const isFrench = i18n.language?.startsWith('fr');

  const [selectedEvent, setSelectedEvent] = useState<EngagementEvent | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  const getFormType = (apiEvent: any) => {
    // First check if it's a CEU event (either by type or subtype)
    if (apiEvent?.type?.toLowerCase() === 'ceu' || apiEvent?.eventSubtype?.toLowerCase() === 'ceu') {
      return 'ceu';
    }
    
    if (apiEvent?.eventSubtype) {
      if (apiEvent.eventSubtype === 'Public Ownership Experience') return 'public-ownership-experience';
      if (apiEvent.eventSubtype === 'Dealer Training') return 'dealer-training';
      if (apiEvent.eventSubtype === 'Public Introduction Class') return 'event-public-intro-class';
      return `event-${apiEvent.eventSubtype.toLowerCase().replace(/ /g, '-')}`;
    }
    
    if (apiEvent?.name?.toLowerCase().includes('public introduction class')) return 'event-public-intro-class';
    return 'event-default';
  };

  const handleRegister = (event: EngagementEvent) => {
    if (event.actionType === 'register' && event.details) {
      const apiEvent = apiEvents?.find(e => e.appointmentId === event.id);
      const formType = getFormType(apiEvent);

      setSelectedEvent({ ...event, formType });
      setShowDetailsModal(true);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedEvent(null);
  };

  const handleBook = () => {
    setShowDetailsModal(false);
    setShowRegistrationForm(true);
    setCurrentStep(0);
  };

  const handleStepComplete = (stepData: Record<string, any>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const handleCloseRegistrationForm = () => {
    setShowRegistrationForm(false);
    setSelectedEvent(null);
  };

  // Calculate total number of events to display
  const calculateTotalEvents = () => {
    // Count hardcoded video events (always 2)
    const hardcodedEventsCount = 2;
    
    // Count API events (filter out null entries)
    const apiEventsCount = Array.isArray(apiEvents) 
      ? apiEvents.filter(event => event?.appointmentId).length 
      : 0;
    
    return hardcodedEventsCount + apiEventsCount;
  };

  const totalEvents = calculateTotalEvents();
  const shouldHideScrollbar = totalEvents <= 5;

  return (
    <section className="engagement-events">
      <h2 className="engagement-events__title">
        {t('engagement.eventsTitle')}
      </h2>
      <div className={`engagement-events__list ${shouldHideScrollbar ? 'hide-scrollbar' : ''}`}>
        {/* Hardcoded video events */}
        <EventCard 
          key="training-module" 
          event={{
            id: 'training-module',
            title: 'Online Training Module',
            points: 5,
            actionType: 'video',
            eventType: 'training'
          }} 
          onRegister={handleRegister}
        />
        <EventCard 
          key="spotlight-video" 
          event={{
            id: 'spotlight-video',
            title: 'Spotlight video',
            points: 7,
            actionType: 'video',
            eventType: 'training'
          }} 
          onRegister={handleRegister}
        />
        
        {/* API Events */}
        {shouldShowEvents ? (
          isLoading ? (
          <div className="engagement-events__loading">{t('common.loading', 'Loading...')}</div>
        ) : error ? (
          <div className="engagement-events__error">{t('common.error', 'Error loading events')}</div>
        ) : Array.isArray(apiEvents) ? apiEvents.map((apiEvent) => {
          if (!apiEvent?.appointmentId) {
            return null;
          }
          
          // Use language-specific fields if available
          const eventName = isFrench && apiEvent.nameFr ? apiEvent.nameFr : apiEvent.name;
          const eventDescription = isFrench && apiEvent.descriptionFr ? apiEvent.descriptionFr : apiEvent.description;
          
          return (
            <EventCard 
              key={apiEvent.appointmentId} 
              event={{
                id: String(apiEvent.appointmentId),
                title: eventName || t('common.untitled', 'Untitled Event'),
                points: apiEvent.pointsToEarn || 0,
                actionType: 'register',
                eventType: 'store',
                details: {
                  date: new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  }), // Using current date since API doesn't provide date yet
                  time: '10:00 AM', // Default time since API doesn't provide this yet
                  duration: (apiEvent.duration && apiEvent.duration > 0 && apiEvent.durationUnit) ? 
                    `${apiEvent.duration} ${apiEvent.durationUnit.toLowerCase()}` : 
                    '60 minutes',
                  type: normalizeEventType(apiEvent.eventType),
                  description: eventDescription || 'No description available',
                  location: typeof apiEvent.location === 'object' && Object.keys(apiEvent.location).length === 0 ? 
                    `${apiEvent.city} Showroom` : // If location is empty object, use city
                    (typeof apiEvent.location === 'string' ? apiEvent.location : ''),
                  city: apiEvent.city || '',
                  showroom: `${apiEvent.city} Showroom` // Construct showroom from city
                }
              }} 
              isAttended={apiEvent.isAttended}
              isBooked={apiEvent.isBooked}
              onRegister={handleRegister} 
            />
          );
        }) : (
          <div className="engagement-events__error">{t('common.noEvents', 'No events available')}</div>
        )) : null}
      </div>

      {selectedEvent && (
          <>
            <EventDetailsModal
              event={selectedEvent}
              isOpen={showDetailsModal}
              onClose={handleCloseDetailsModal}
              onBook={handleBook}
            />
            {showRegistrationForm && (
              <MultiStepForm
                initialStep={currentStep}
                steps={[
                  {
                    id: 'event',
                    icon: { src: formIconCeu, alt: 'Event Icon' },
                    title: selectedEvent.title,
                    isCompleted: true,
                    Component: <CompletedStep onNext={() => setCurrentStep(prev => prev + 1)} />
                  },
                  {
                    id: 'user',
                    icon: { src: formIconInfo, alt: 'User Icon' },
                    title: t('form.steps.yourInformation'),
                    Component: <UserInfoStep 
                      onNext={(data) => {
                        handleStepComplete(data);
                        setCurrentStep(prev => prev + 1);
                      }}
                      initialData={formData}
                      namespace="events"
                      formType={selectedEvent.formType}
                    />
                  },
                  {
                    id: 'confirm',
                    icon: { 
                      src: (isReached: boolean) => 
                        isReached 
                          ? formIconConfirmReached
                          : formIconConfirmNotReached,
                      alt: 'Confirmation Icon'
                    },
                    title: t('form.steps.reviewAndConfirm'),
                    Component: <ConfirmationStep 
                      eventData={{
                        remoteObjectId: selectedEvent.id,
                        ...selectedEvent,
                        type: (selectedEvent.details?.type || 'In-Person') as EventType,
                        duration: selectedEvent.details?.duration || '',
                        description: selectedEvent.details?.description || '',
                        date: selectedEvent.details?.date || '',
                        time: selectedEvent.details?.time || '',
                        location: selectedEvent.details?.location || '',
                        city: (selectedEvent.details?.city || 'Toronto') as CityType,
                        showroom: (selectedEvent.details?.showroom || 'Subzero Toronto Showroom') as ShowroomType,
                        imageUrl: defaultEventImage,
                        imageEventType: 'ceu' as EventImageType
                      }}
                      initialData={formData}
                      onNext={handleStepComplete}
                      variant="event"
                    />
                  }
                ]}
                onClose={handleCloseRegistrationForm}
                onComplete={async (data) => {
                  if (!selectedEvent) return;
                  
                  return new Promise<void>((resolve, reject) => {
                    bookEvent({
                      eventId: selectedEvent.id,
                      firstname: data.firstName || '',
                      lastname: data.lastName || '',
                      email: data.email || '',
                      phone: data.phone || '',
                      company: data.company || '',
                      // Field mappings to match EventCard.tsx
                      foodDietaryRestriction: data.foodAllergies || '',
                      ...(data.guestName ? { guestName: data.guestName } : {}),
                      productsInterest: data.productsInterested || '',
                      applianceRetailer: data.retailer || '',
                      additionalComments: data.notes || data.additionalComments || '',
                      requireCreditReport: data.creditReporting || null,
                      productsRelevant: data.productsOwned || null,
                      expectation: data.trainingGoals || null,
                      alreadyAttented: data.previousTraining || null,
                      interestForScheduling: data.privateSession || null,
                      brandSelled: data.currentBrands || null
                    }, {
                      onSuccess: () => {
                        handleCloseRegistrationForm();
                        setShowConfirmation(true);
                        resolve();
                      },
                      onError: (error) => {
                        console.error('Booking error:', error);
                        reject(error);
                      }
                    });
                  });
                }}
                variant="event"
              />
            )}
          </>
        )}
        {showConfirmation && (
          <BookingConfirmation onClose={() => setShowConfirmation(false)} />
        )}
    </section>
  );
};
